













































































































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  useContext,
} from '@nuxtjs/composition-api';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { required, min, digits } from 'vee-validate/dist/rules';
import {
  AttributesMetadataOutput,
  Country,
  CountryCodeEnum,
  CustomAttributeOptionInterface,
  useCountrySearch,
  useUser,
} from '~/composables';
import {
  CheckoutAddressForm,
  getInitialCheckoutAddressForm,
} from '~/helpers/checkout/address';
import addressGetter from '~/modules/customer/getters/addressGetter';
import { getCustomAttributeOptions } from '~/modules/customer/helpers/getCustomAttributeOptions';
import { validateNip } from '~/helpers/validateNip';
import { addBasePath } from '~/helpers/addBasePath';
import {
  customerPasswordRegExp,
  invalidPasswordMsg,
} from '~/modules/customer/helpers/passwordValidation';
import { transformUserCreateAddressInput } from '~/modules/customer/helpers/userAddressManipulator';
import { generateUserData } from '~/modules/customer/helpers/generateUserData';
import { B2BRegisterFormFields } from '~/modules/customer/components/LoginModal/forms/types';
import CompanyBillingAddressForm from '../CompanyBillingAddressForm.vue';
import FormalConsents, { Consents } from '../FormalConsents.vue';
import {
  SfSelect,
  SfButton,
  SfInput,
  SfCheckbox,
  SfSelectOption,
  SfImage,
} from '../StorefrontUi';

extend('required', {
  ...required,
  message: 'This field is required',
});
extend('min', {
  ...min,
  message: 'The field should have at least {length} characters',
});
extend('digits', {
  ...digits,
  message: 'Please provide a valid phone number',
});
extend('vatid', {
  message: 'Please provide a valid NIP',
  validate: validateNip,
});
extend('password', {
  message: invalidPasswordMsg,
  validate: (value: string) => customerPasswordRegExp.test(value),
});

export interface B2BRegistrationFormValues extends CheckoutAddressForm {
  email: string;
  password: string;
  partnership_type: string;
  sales_channel?: string[];
  consent_to_rules: boolean;
  consent_to_email_commercials: boolean;
  consent_to_phone_commercials: boolean;
}

export default defineComponent({
  components: {
    ValidationObserver,
    ValidationProvider,
    CompanyBillingAddressForm,
    SfButton,
    SfInput,
    SfSelect,
    SfCheckbox,
    FormalConsents,
    SfSelectOption,
    SfImage,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(_, { emit }) {
    const { load: loadCountries } = useCountrySearch();
    const { i18n } = useContext();
    const { getCustomAttributeMetadataV2 } = useUser();

    const companyRegistrationData = ref<B2BRegistrationFormValues>({
      ...getInitialCheckoutAddressForm(),
      email: '',
      password: '',
      partnership_type: '',
      sales_channel: [],
      consent_to_rules: false,
      consent_to_email_commercials: false,
      consent_to_phone_commercials: false,
    });
    const formObserver = ref<any>(null);
    const billingDetails = ref<CheckoutAddressForm>(
      getInitialCheckoutAddressForm(),
    );
    const countries = ref<Country[]>([]);
    const salesChannelError = ref<string | null>(null);
    const isRequiredSalesChannel = computed(() => companyRegistrationData.value.partnership_type === 'distribution' && companyRegistrationData.value.sales_channel.length === 0);
    const customAttributesMetadataV2 = ref<AttributesMetadataOutput | null>(null);
    const salesChannelOptions = ref<CustomAttributeOptionInterface[]>([]);
    const cooperationTypeOptions = ref<CustomAttributeOptionInterface[]>([]);

    const countriesList = computed(() => addressGetter.countriesList(countries.value));

    const prepareDataForRegistration = (): B2BRegisterFormFields => {
      const userData = generateUserData({
        ...companyRegistrationData.value,
        partnership_type: Number(companyRegistrationData.value.partnership_type),
        sales_channel: companyRegistrationData.value.sales_channel.length > 0 ? companyRegistrationData.value.sales_channel.map(Number) : [],
        firstname: billingDetails.value.firstname,
        lastname: billingDetails.value.lastname,
      });

      const { building, ...addressDetails } = billingDetails.value;
      addressDetails.apartment = addressDetails.apartment ? `${building} / ${addressDetails.apartment}` : building;
      const addressData = transformUserCreateAddressInput({ address: addressDetails });

      addressData.country_id = addressDetails.country_code as CountryCodeEnum;

      addressData.region = {
        region: addressData.region as string || '',
        region_code: '',
        region_id: null,
      };

      return {
        ...userData,
        addresses: [{
          ...addressData,
        }],
      } as B2BRegisterFormFields;
    };

    const handleFormSubmit = () => {
      const dataForRegistration = prepareDataForRegistration();
      emit('submit', dataForRegistration);
    };

    const changeBillingDetails = (newBillingAddress: CheckoutAddressForm) => {
      billingDetails.value = newBillingAddress;
    };

    const toggleSalesChannel = (value: string) => {
      const salesChannels = companyRegistrationData.value.sales_channel;
      const index = salesChannels.indexOf(value);

      if (index === -1) {
        salesChannels.push(value);
      } else {
        salesChannels.splice(index, 1);
      }
      salesChannelError.value = salesChannels.length === 0
        ? (i18n.t('Please select at least one sales channel') as string)
        : null;
    };

    const changeCompanyRegistrationData = (
      field: keyof B2BRegistrationFormValues,
      value: string,
    ) => {
      if (field === 'sales_channel') {
        toggleSalesChannel(value);
        return;
      }
      if (!field.includes('consent')) {
        (companyRegistrationData.value[field] as string) = value;
        if (field === 'partnership_type' && value !== 'distribution') {
          companyRegistrationData.value.sales_channel = [];
        }
      }
    };

    const handleConsentsChange = (consents: Consents) => {
      const { rules, emailCommercials, phoneCommercials } = consents;
      companyRegistrationData.value.consent_to_rules = rules;
      companyRegistrationData.value.consent_to_email_commercials = emailCommercials;
      companyRegistrationData.value.consent_to_phone_commercials = phoneCommercials;
    };

    onMounted(async () => {
      const loadedCountries = await loadCountries();
      countries.value = loadedCountries;
      customAttributesMetadataV2.value = await getCustomAttributeMetadataV2(['partnership_type', 'sales_channel']);
      cooperationTypeOptions.value = getCustomAttributeOptions(customAttributesMetadataV2.value.items, 'partnership_type');
      salesChannelOptions.value = getCustomAttributeOptions(customAttributesMetadataV2.value.items, 'sales_channel');
    });

    return {
      formObserver,
      handleFormSubmit,
      billingDetails,
      changeBillingDetails,
      countriesList,
      changeCompanyRegistrationData,
      companyRegistrationData,
      cooperationTypeOptions,
      salesChannelOptions,
      handleConsentsChange,
      salesChannelError,
      isRequiredSalesChannel,
      addBasePath,
    };
  },
});
